/* eslint-disable no-magic-numbers */
import {
  Box3,
  Euler,
  MathUtils,
  Raycaster,
  Vector2,
  Vector3,
  Web3DMeshMaterial,
  Plane,
  Face,
} from '@technology/web3d-plugin-threejs';
import { QuantizedMeshSettings } from './config/qm-settings';
import { LRUCache } from 'lru-cache';
import { calculateGpuMemoryCacheSize } from '@technology/web3d/dist/esnext/Helpers/caching.js';
import { Api } from '@technology/web3d/dist/esnext/Api.js';
import {
  Geometry,
  GeometryModel,
  IIntersection,
  Mesh,
  NavigationPickableModel,
  PickableModel,
  type GeometryType,
  type SnapType,
  type Web3DEventName,
} from '@technology/web3d';
const SNAP_TYPE_FACE = 0;
// GeometryType.MESH (0). Inlined to avoid importing the const enum value (isolatedModules).
const GEOMETRY_TYPE_MESH = 0;
// Web3DEventName is an ambient const enum, which cannot be accessed with isolatedModules.
const NAVIGATION_START_EVENT =
  'navigationstart' as Web3DEventName.NavigationStart;
const NAVIGATION_END_EVENT = 'navigationend' as Web3DEventName.NavigationEnd;
import { Caster } from '@technology/web3d/dist/esnext/Picker/Caster.js';
import proj4 from 'proj4';
import { QmDecoder } from './processing/qm-decoder';
import { QuantizedMeshData } from './types/quantized-mesh-data';
import { approximateQuantizedMeshDataSize } from './processing/qm-factory';
import { QMNode } from './qm-node';
import { TileCache } from './qm-tile-cache';
import { ObjectPool } from './object-pool';
import {
  PromiseQueue,
  type PromiseQueueDebugSnapshot,
} from './processing/promise-queue';
import { TextureImage } from './types/texture-image';
import { GenericModel } from '@technology/web3d/dist/esnext/GenericModel';
import { getLogger } from '@trimble/logging';
import { MeshCommitQueue } from './processing/mesh-commit-queue';
import type { TileMesh } from './qm-node';
import { UpdateScheduler } from './processing/update-scheduler';

const REFERENCE_SYSTEM = proj4.Proj('WGS84');
const QM_DATA_QUEUE_CONCURRENCY = 10;
const QM_DATA_QUEUE_MAX_QUEUED_LENGTH = 100;
const TEXTURE_QUEUE_CONCURRENCY = 8;
const TEXTURE_QUEUE_MAX_QUEUED_LENGTH = 100;
const QUEUE_DEBUG_SAMPLE_INTERVAL_MS = 500;
const QUEUE_DEBUG_LOG_WINDOW_MS = 2_000;
const MISSING_CHILD_COVERAGE_SCREEN_SIZE_FACTOR = 0.5;
const MISSING_CHILD_COVERAGE_EDGE_NDC_MARGIN = 0.1;
const LARGE_LOW_RES_FALLBACK_SCREEN_SIZE_FACTOR = 4;

type QueueDebugTextureSnapshot = PromiseQueueDebugSnapshot & {
  id: string;
};

type QueueDebugThroughputStats = {
  id: string;
  type: string;
  completedRequestsInWindow: number;
  averageCompletedRequestsPerSecond: number;
};

type QueueDebugThroughput = {
  qmDataQueue: QueueDebugThroughputStats;
  textureQueues: QueueDebugThroughputStats[];
};

type QueueDebugTaskSnapshot = PromiseQueueDebugSnapshot['queuedTasks'][number];

type TileQueueDebugMatch = {
  queueId: string;
  queueType: string;
  key: string;
  lifecycle: 'queued' | 'active';
  position: number | null;
  ageMs: number;
  priority: number;
  aborted: boolean;
  abortReason: QueueDebugTaskSnapshot['abortReason'];
};

type VisibleNonRenderableTileDebugSnapshot = {
  total: number;
  unmatched: number;
  byState: Record<string, number>;
  tiles: Array<{
    tileId: string;
    parentTileId: string | undefined;
    state: string;
    screenSizePx: number;
    priority: number;
    hasMesh: boolean;
    textureId: string | undefined;
    blockingReason: 'noMesh' | 'stateNotRenderable';
    queues: {
      qmData: TileQueueDebugMatch[];
      textures: TileQueueDebugMatch[];
    };
  }>;
};

type ClickTileDebugInfo = {
  tileId: string;
  parentTileId: string | undefined;
  state: string;
  isVisibleInFrame: boolean;
  lastTouchedFrame: number;
  hasMesh: boolean;
  textureId: string | undefined;
  screenSizePx: number;
  priority: number;
};

type ClickTileDescendantDebugInfo = ClickTileDebugInfo & {
  depth: number;
  queues: {
    qmData: TileQueueDebugMatch[];
    textures: TileQueueDebugMatch[];
  };
};

type ClickTileSubtreeDebugSnapshot = {
  frame: number;
  textureId: string;
  clickedTile: ClickTileDebugInfo;
  descendants: ClickTileDescendantDebugInfo[];
  summary: {
    totalDescendants: number;
    visibleDescendants: number;
    queuedOrActiveDescendants: number;
    unmatchedVisibleLoadingDescendants: number;
    byState: Record<string, number>;
  };
};

type QueueDebugStateSnapshot = {
  timestampMs: number;
  frame: number;
  textureId: string;
  renderedTiles: number;
  staleRenderedTiles: number;
  qmDataQueue: PromiseQueueDebugSnapshot;
  textureQueues: QueueDebugTextureSnapshot[];
  rootTileCounts: Array<{
    tileId: string;
    tiles: number;
  }>;
};

// Create a reusable Euler instance for camera constraint calculations
const euler = new Euler(0, 0, 0, 'ZYX');
const maxPitch = Math.PI / 2; // Prevent looking above the XY plane (horizon).
const minPitch = 0; // Allow looking straight down.

export class QuantizedMeshModel
  extends GenericModel
  implements PickableModel, NavigationPickableModel, GeometryModel
{
  private disposed = false;
  private readonly tileTreeRoots: Array<QMNode>;

  private readonly textureCache!: LRUCache<number, ImageBitmap>;
  private readonly qmDataCache: LRUCache<number, QuantizedMeshData>;
  private readonly qmDecoder!: QmDecoder;

  private readonly intervalHandle: ReturnType<typeof setInterval> | null = null;
  private readonly queueDebugLogIntervalHandle: ReturnType<
    typeof setInterval
  > | null = null;
  private readonly queueDebugStateHistory: QueueDebugStateSnapshot[] = [];

  private readonly raycaster = new Raycaster();
  private readonly mouse = new Vector2();
  private readonly projectedTileCorner = new Vector3();
  // TODO: Implement wrapping so that long running sessions do not overflow the frameNumber
  private frameNumber = 0;
  // private frameStart = 0;
  private readonly tileCache: TileCache;
  private renderInProgress = false;
  private readonly objectPool = new ObjectPool();
  // This flag is used to ensure that some housekeeping tasks are done
  // only once, after the first tiles have been rendered.
  private firstTilesRendered = false;
  private isNavigating = false;

  private readonly qmDataPromiseQueue = new PromiseQueue<QuantizedMeshData>(
    QM_DATA_QUEUE_CONCURRENCY,
    'qm-data',
    QM_DATA_QUEUE_MAX_QUEUED_LENGTH
  );
  private readonly textureQueues: Map<string, PromiseQueue<TextureImage>> =
    new Map();
  private readonly meshCommitQueue = new MeshCommitQueue<
    TileMesh | undefined
  >();
  private readonly updateScheduler = new UpdateScheduler(() => {
    this.renderScene();
  });

  private readonly constrainCameraMovementFn =
    this.constrainCameraMovement.bind(this);
  private readonly navigationStartedFn = (): void => {
    this.isNavigating = true;
    this.updateScheduler.request();
  };
  private readonly navigationEndedFn = (): void => {
    this.isNavigating = false;
    this.updateScheduler.request();
  };
  private readonly cameraChangedFn = (): void => {
    this.updateScheduler.request();
  };

  private constrainCameraMovement(): void {
    euler.setFromQuaternion(this.api.camera!.quaternion);
    if (euler.x < minPitch || euler.x > maxPitch) {
      euler.x = MathUtils.clamp(euler.x, minPitch, maxPitch);
      this.api.camera!.quaternion.setFromEuler(euler);
    }

    const cameraHeight = this.api.camera!.position.z;
    const baseHeight = this.qmDecoder.baseHeight ?? 0.0;
    if (cameraHeight < baseHeight + 10) {
      this.api.camera!.position.z = baseHeight + 10;
    }
  }

  private mapModeBasePlane: Plane | undefined;

  constructor(
    private readonly mapSettings: QuantizedMeshSettings,
    private readonly api: Api,
    decodeWorkerUrl: string
  ) {
    super(mapSettings.options.modelId, api);
    if (!this.api.camera) {
      throw new Error('QuantizedMeshSMModel: api.camera is not defined');
    }

    if (this.mapSettings.options.constrainCamera) {
      api.camera?.subscribe(this.constrainCameraMovementFn);
    }
    api.camera.subscribe(this.cameraChangedFn);
    api.eventDispatcher?.subscribe(
      NAVIGATION_START_EVENT,
      this.navigationStartedFn
    );
    api.eventDispatcher?.subscribe(
      NAVIGATION_END_EVENT,
      this.navigationEndedFn
    );

    this.qmDecoder = new QmDecoder(
      decodeWorkerUrl,
      this.mapSettings.tileFetcher.fetcher,
      this.mapSettings.options.tileOptions.flatZoomLevels,
      this.mapSettings.options.meshSettings.baseHeight
    );

    if (this.mapSettings.tileOptions.initialTile) {
      this.qmDecoder.initialTile = this.mapSettings.tileOptions.initialTile;
    }

    this.mapSettings.updateTextureId();

    this.textureCache = this.createTextureCache(this.mapSettings.imageSize);
    this.qmDataCache = this.createQMDataCache();
    this.tileTreeRoots = [];
    this.tileCache = new TileCache();

    // Create a tileTreeRoot for each tile adjacent to the root tile.
    // This is necessary to ensure that the sites that straddle the border
    // of the root tile can also be viewed in their entirety.
    const rootCoordinate = this.mapSettings.options.tileOptions.rootCoordinate;
    const maxCoordinateAtZoom =
      Math.pow(2, this.mapSettings.options.tileOptions.rootZoomLevel) - 1;

    for (let x = -1; x <= 1; x++) {
      for (let y = -1; y <= 1; y++) {
        const coordinate = new Vector2(
          rootCoordinate.x + x,
          rootCoordinate.y + y
        );

        // Wrap coordinates to ensure they stay within the valid range.
        // This would cause the tiles to wrap around the world if the edge is reached.
        if (coordinate.x < 0) coordinate.x = maxCoordinateAtZoom;
        if (coordinate.y < 0) coordinate.y = maxCoordinateAtZoom;
        if (coordinate.x > maxCoordinateAtZoom) coordinate.x = 0;
        if (coordinate.y > maxCoordinateAtZoom) coordinate.y = 0;

        const tileTreeRoot = new QMNode(
          api,
          mapSettings,
          this.root,
          this.mapSettings.options.tileOptions.rootZoomLevel,
          coordinate,
          null,
          this.textureCache,
          this.qmDataCache,
          this.qmDecoder,
          this.objectPool,
          this.textureQueues,
          this.qmDataPromiseQueue,
          this.meshCommitQueue,
          (delayMs) => this.updateScheduler.request(delayMs),
          undefined,
          TEXTURE_QUEUE_CONCURRENCY,
          TEXTURE_QUEUE_MAX_QUEUED_LENGTH
        );
        this.tileTreeRoots.push(tileTreeRoot);
      }
    }

    this.updateScheduler.request();
    // This is used for stepping through the rendering process while debugging.
    // window.addEventListener('mouseup', (evt) => {
    //   if (evt.button === 2) {
    //     console.log('Setting renderScene on right mouse up');
    //     this.renderScene();
    //   }
    // });

    if (this.mapSettings.debug) {
      window.addEventListener('click', (e) => {
        this.debugHandleMouseClick(e);
      });
      this.recordQueueDebugState();
      this.intervalHandle = setInterval(() => {
        this.recordQueueDebugState();
      }, QUEUE_DEBUG_SAMPLE_INTERVAL_MS);
      this.queueDebugLogIntervalHandle = setInterval(() => {
        this.logRollingQueueDebugState();
      }, QUEUE_DEBUG_LOG_WINDOW_MS);
    }
  }

  private renderedTiles = new Set<QMNode>();

  /**
   * Listeners notified whenever the set of tiles attached to {@link root}
   * changes (a tile is added or removed in {@link renderScene}). Consumers
   * such as the section profile pipeline use this to re-extract geometry
   * once new LOD has loaded in. Consumers should debounce their reaction —
   * during continuous panning this can fire many times per second.
   */
  private readonly tilesChangedListeners = new Set<() => void>();

  /**
   * Subscribe to be notified when the rendered tile set of this model
   * changes (LOD shifts, new tiles streaming in, tiles dropping out of
   * view). Returns an unsubscribe function.
   */
  public onTilesChanged(callback: () => void): () => void {
    this.tilesChangedListeners.add(callback);
    return () => {
      this.tilesChangedListeners.delete(callback);
    };
  }

  private notifyTilesChanged(): void {
    for (const cb of this.tilesChangedListeners) {
      try {
        cb();
      } catch (e) {
        getLogger().error(
          '[QuantizedMeshModel] onTilesChanged listener threw',
          e
        );
      }
    }
  }

  private createQueueDebugStateSnapshot(
    timestampMs: number
  ): QueueDebugStateSnapshot {
    const staleRenderedTiles = Array.from(this.renderedTiles).filter(
      (tile) => tile.textureId !== this.mapSettings.textureId
    );
    const rootTileCounts = this.tileTreeRoots.map((root) => ({
      tileId: root.tileId,
      tiles: root.countTilesInTree(),
    }));

    return {
      timestampMs,
      frame: this.frameNumber,
      textureId: this.mapSettings.textureId,
      renderedTiles: this.renderedTiles.size,
      staleRenderedTiles: staleRenderedTiles.length,
      qmDataQueue: this.qmDataPromiseQueue.getDebugSnapshot(
        QUEUE_DEBUG_LOG_WINDOW_MS
      ),
      textureQueues: Array.from(this.textureQueues.entries()).map(
        ([id, queue]) => ({
          id,
          ...queue.getDebugSnapshot(QUEUE_DEBUG_LOG_WINDOW_MS),
        })
      ),
      rootTileCounts,
    };
  }

  private recordQueueDebugState(): void {
    const now = performance.now();
    this.pruneQueueDebugHistory(now);
    this.queueDebugStateHistory.push(this.createQueueDebugStateSnapshot(now));
    this.pruneQueueDebugHistory(now);
  }

  private pruneQueueDebugHistory(now: number): void {
    const minTimestamp = now - QUEUE_DEBUG_LOG_WINDOW_MS;
    while (
      this.queueDebugStateHistory.length > 0 &&
      this.queueDebugStateHistory[0].timestampMs < minTimestamp
    ) {
      this.queueDebugStateHistory.shift();
    }
  }

  private getQueueDebugThroughput(): QueueDebugThroughput {
    const latestSnapshot =
      this.queueDebugStateHistory[this.queueDebugStateHistory.length - 1];

    return {
      qmDataQueue: this.getQueueDebugThroughputStats(
        'qm-data',
        latestSnapshot?.qmDataQueue
      ),
      textureQueues: latestSnapshot
        ? latestSnapshot.textureQueues.map((latestTextureQueue) =>
            this.getQueueDebugThroughputStats(
              latestTextureQueue.id,
              latestTextureQueue
            )
          )
        : [],
    };
  }

  private getQueueDebugThroughputStats(
    id: string,
    snapshot: PromiseQueueDebugSnapshot | undefined
  ): QueueDebugThroughputStats {
    return {
      id,
      type: snapshot?.type ?? id,
      completedRequestsInWindow: snapshot?.completedInWindow ?? 0,
      averageCompletedRequestsPerSecond:
        snapshot?.averageCompletedRequestsPerSecond ?? 0,
    };
  }

  private logRollingQueueDebugState(): void {
    const now = performance.now();
    this.recordQueueDebugState();
    this.pruneQueueDebugHistory(now);
    const latestSnapshot =
      this.queueDebugStateHistory[this.queueDebugStateHistory.length - 1];

    getLogger().info('[QuantizedMeshModel] rolling queue state', {
      windowMs: QUEUE_DEBUG_LOG_WINDOW_MS,
      sampleIntervalMs: QUEUE_DEBUG_SAMPLE_INTERVAL_MS,
      samples: this.queueDebugStateHistory.length,
      queueThroughput: this.getQueueDebugThroughput(),
      visibleNonRenderableTiles:
        this.getVisibleNonRenderableTileDebugSnapshot(latestSnapshot),
      history: this.queueDebugStateHistory,
    });
  }

  private getVisibleNonRenderableTileDebugSnapshot(
    queueSnapshot: QueueDebugStateSnapshot | undefined
  ): VisibleNonRenderableTileDebugSnapshot {
    const tiles: VisibleNonRenderableTileDebugSnapshot['tiles'] = [];
    const byState: Record<string, number> = {};

    for (const root of this.tileTreeRoots) {
      root.traverseAll((node) => {
        if (!this.isVisibleNonRenderableTile(node)) {
          return;
        }

        const queues = this.getTileQueueDebugMatches(node, queueSnapshot);
        const stateCount = byState[node.state] ?? 0;
        byState[node.state] = stateCount + 1;
        tiles.push({
          tileId: node.tileId,
          parentTileId: node.parent?.tileId,
          state: node.state,
          screenSizePx: node.calculateTileScreenSize(),
          priority: node.tilePriority(),
          hasMesh: node.mesh != null,
          textureId: node.textureId,
          blockingReason: node.mesh ? 'stateNotRenderable' : 'noMesh',
          queues,
        });
      });
    }

    return {
      total: tiles.length,
      unmatched: tiles.filter(
        (tile) =>
          tile.queues.qmData.length === 0 && tile.queues.textures.length === 0
      ).length,
      byState,
      tiles,
    };
  }

  private isVisibleNonRenderableTile(node: QMNode): boolean {
    if (!node.isVisibleInFrame) {
      return false;
    }

    if (!node.mesh) {
      return true;
    }

    return node.state === 'start' || node.state === 'loading';
  }

  private getTileQueueDebugMatches(
    node: QMNode,
    queueSnapshot: QueueDebugStateSnapshot | undefined
  ): VisibleNonRenderableTileDebugSnapshot['tiles'][number]['queues'] {
    const tileKeyFragment = this.getTileQueueKeyFragment(node);
    return {
      qmData: queueSnapshot
        ? this.getQueueDebugMatches(
            'qm-data',
            queueSnapshot.qmDataQueue,
            tileKeyFragment
          )
        : [],
      textures: queueSnapshot
        ? queueSnapshot.textureQueues.flatMap((textureQueue) =>
            this.getQueueDebugMatches(
              textureQueue.id,
              textureQueue,
              tileKeyFragment
            )
          )
        : [],
    };
  }

  private getTileQueueKeyFragment(node: QMNode): string {
    return node.queueKeyFragment;
  }

  private getQueueDebugMatches(
    queueId: string,
    queueSnapshot: PromiseQueueDebugSnapshot,
    tileKeyFragment: string
  ): TileQueueDebugMatch[] {
    return [
      ...this.getQueueTaskDebugMatches(
        queueId,
        queueSnapshot.type,
        'queued',
        queueSnapshot.queuedTasks,
        tileKeyFragment
      ),
      ...this.getQueueTaskDebugMatches(
        queueId,
        queueSnapshot.type,
        'active',
        queueSnapshot.activeTasks,
        tileKeyFragment
      ),
    ];
  }

  private getQueueTaskDebugMatches(
    queueId: string,
    queueType: string,
    lifecycle: 'queued' | 'active',
    tasks: QueueDebugTaskSnapshot[],
    tileKeyFragment: string
  ): TileQueueDebugMatch[] {
    return tasks
      .filter((task) => task.key.includes(tileKeyFragment))
      .map((task) => ({
        queueId,
        queueType,
        key: task.key,
        lifecycle,
        position: task.position,
        ageMs: task.ageMs,
        priority: task.priority,
        aborted: task.aborted,
        abortReason: task.abortReason,
      }));
  }

  private getClickTileSubtreeDebugSnapshot(
    clickedNode: QMNode,
    queueSnapshot: QueueDebugStateSnapshot
  ): ClickTileSubtreeDebugSnapshot {
    const descendants: ClickTileDescendantDebugInfo[] = [];
    const byState: Record<string, number> = {};

    clickedNode.traverseDescendantsWithDepth((node, depth) => {
      const queues = this.getTileQueueDebugMatches(node, queueSnapshot);
      const stateCount = byState[node.state] ?? 0;
      byState[node.state] = stateCount + 1;
      descendants.push({
        ...this.getClickTileDebugInfo(node),
        depth,
        queues,
      });
    });

    return {
      frame: this.frameNumber,
      textureId: this.mapSettings.textureId,
      clickedTile: this.getClickTileDebugInfo(clickedNode),
      descendants,
      summary: {
        totalDescendants: descendants.length,
        visibleDescendants: descendants.filter((node) => node.isVisibleInFrame)
          .length,
        queuedOrActiveDescendants: descendants.filter(
          (node) =>
            node.queues.qmData.length > 0 || node.queues.textures.length > 0
        ).length,
        unmatchedVisibleLoadingDescendants: descendants.filter(
          (node) =>
            node.isVisibleInFrame &&
            (node.state === 'loading' || node.state === 'start') &&
            node.queues.qmData.length === 0 &&
            node.queues.textures.length === 0
        ).length,
        byState,
      },
    };
  }

  private getClickTileDebugInfo(node: QMNode): ClickTileDebugInfo {
    return {
      tileId: node.tileId,
      parentTileId: node.parent?.tileId,
      state: node.state,
      isVisibleInFrame: node.isVisibleInFrame,
      lastTouchedFrame: node.lastTouchedFrame,
      hasMesh: node.mesh != null,
      textureId: node.textureId,
      screenSizePx: node.calculateTileScreenSize(),
      priority: node.tilePriority(),
    };
  }

  private renderScene(): void {
    if (this.renderInProgress) {
      console.log('======== render in progress, skipping this frame ========');
      return;
    }

    if (this.disposed) {
      return;
    }

    // console.log('textureQueue length', this.textureFetcherPromiseQueue.length);
    // console.log('qmQueue length', this.textureFetcherPromiseQueue.length);

    this.renderInProgress = true;
    // this.frameStart = performance.now();

    this.tileTreeRoots.forEach((t) => {
      t.update(
        this.frameNumber,
        this.api.camera!.frustum,
        this.firstTilesRendered,
        this.isNavigating
      );
    });

    // for (const root of this.tileTreeRoots) {
    //   if (root.countTilesInTree() > 30) {
    //     console.log('active tree', root);
    //   }
    // }

    const renderableTiles: QMNode[] = [];
    this.tileTreeRoots.forEach((t) => {
      this.collectRenderableTiles(t, renderableTiles);
    });
    let promotedReadyTile = false;
    for (const tile of renderableTiles) {
      if (tile.state === 'ready') {
        tile.transitionTo('rendered');
        promotedReadyTile = true;
      }
      tile.ensureLatestTexture(true);
      if (!this.tileCache.get(tile.tileId)) {
        this.tileCache.add(tile);
      }
    }

    const newRenderableSet = new Set(renderableTiles);

    const tilesToAdd: QMNode[] = [];
    for (const tile of renderableTiles) {
      if (!this.renderedTiles.has(tile)) {
        tilesToAdd.push(tile);
      }
    }

    const tilesToRemove: QMNode[] = [];
    for (const tile of this.renderedTiles) {
      if (!newRenderableSet.has(tile)) {
        tilesToRemove.push(tile);
      }
    }

    this.renderedTiles = newRenderableSet;

    // console.log(
    //   'Renderable tiles this frame:',
    //   renderableTiles.length,
    //   renderableTiles
    // );

    tilesToAdd.forEach((t) => {
      this.root.add(t.mesh!);
    });

    if (!this.firstTilesRendered && this.renderedTiles.size > 0) {
      this.firstTilesRendered = true;
      this.handleInitialRender();
    }

    tilesToRemove.forEach((t) => {
      this.root.remove(t.mesh!);
    });

    const renderedTilesChanged =
      tilesToAdd.length > 0 || tilesToRemove.length > 0;
    if (renderedTilesChanged) {
      this.notifyTilesChanged();
      this.updateBoundingBox();
    }

    this.tileCache.prune(this.frameNumber);
    this.frameNumber = (this.frameNumber + 1) % 16_777_216;
    if (promotedReadyTile) {
      this.updateScheduler.request();
    }

    this.renderInProgress = false;
    this.objectPool.reset();
    // console.log(
    //   '======== frame done ========',
    //   performance.now() - this.frameStart
    // );
    // const root = this.tileTreeRoots.find((r) => r.isVisibleInFrame);

    // console.log('Total tiles in tree:', root?.countTilesInTree());
    if (renderedTilesChanged) {
      this.redraw();
    }
  }

  private redraw(): void {
    // Web3D v5 resizes render targets to 0x0 while its container is hidden or
    // not laid out, leaving an incomplete framebuffer for the next draw.
    const container = this.api.container;
    if (container && container.clientWidth > 0 && container.clientHeight > 0) {
      this.api.renderingManager?.redraw();
    }
  }

  private addBasePlane(elevation: number): void {
    this.mapModeBasePlane = new Plane(new Vector3(0, 0, 1), 0);
    this.mapModeBasePlane.setFromNormalAndCoplanarPoint(
      new Vector3(0, 0, 1),
      new Vector3(0, 0, elevation)
    );
  }

  private handleInitialRender(): void {
    this.updateBoundingBox();
    const evt = new CustomEvent('terrainrendered', {
      bubbles: true,
      composed: true,
      cancelable: false,
      detail: {
        baseHeight: this.qmDecoder.baseHeight,
        initialTile: this.qmDecoder.initialTile,
      },
    });
    this.api.eventDispatcher?.dispatch(evt);
    this.addBasePlane(this.qmDecoder.baseHeight ?? 0.0);
    this.updateBoundingBox();
  }

  private collectRenderableTiles(node: QMNode, collection: QMNode[]): void {
    void this.collectRenderableCoverage(node, collection);
  }

  private collectRenderableCoverage(
    node: QMNode,
    collection: QMNode[]
  ): boolean {
    if (!node.isVisibleInFrame) return true;

    if (
      (node.state === 'refining' || node.state === 'refined') &&
      node.children?.length
    ) {
      const childTiles: QMNode[] = [];
      let childrenCovered = true;
      const shouldBoostBlockingChildren =
        this.shouldBoostChildrenForLargeLowResolutionFallback(node);

      for (const child of node.children) {
        const childCovered = this.collectRenderableCoverage(child, childTiles);
        if (!childCovered && !this.canAllowMissingChildCoverageGap(child)) {
          if (shouldBoostBlockingChildren) {
            child.boostFallbackRefinementPrioritySubtree();
          }
          childrenCovered = false;
        }
      }

      if (childrenCovered && childTiles.length > 0) {
        collection.push(...childTiles);
        return true;
      }
    }

    return this.collectFallbackTile(node, collection);
  }

  private shouldBoostChildrenForLargeLowResolutionFallback(
    node: QMNode
  ): boolean {
    if (!node.mesh || !node.isVisibleInFrame) {
      return false;
    }

    const minBoostScreenSize =
      this.mapSettings.imageSize *
      devicePixelRatio *
      LARGE_LOW_RES_FALLBACK_SCREEN_SIZE_FACTOR;

    return node.calculateTileScreenSize() >= minBoostScreenSize;
  }

  private canAllowMissingChildCoverageGap(child: QMNode): boolean {
    if (!child.isVisibleInFrame) {
      return true;
    }

    const maxAllowedScreenSize =
      this.mapSettings.imageSize *
      devicePixelRatio *
      MISSING_CHILD_COVERAGE_SCREEN_SIZE_FACTOR;

    if (child.calculateTileScreenSize() <= maxAllowedScreenSize) {
      return true;
    }

    const edgeVisibleScreenSize =
      this.calculateEdgeVisibleTileScreenSize(child);

    return (
      edgeVisibleScreenSize !== undefined &&
      edgeVisibleScreenSize <= maxAllowedScreenSize
    );
  }

  private calculateEdgeVisibleTileScreenSize(
    child: QMNode
  ): number | undefined {
    const camera = this.api.camera;
    const container = this.api.container;
    if (
      !camera ||
      !container ||
      container.clientWidth <= 0 ||
      container.clientHeight <= 0
    ) {
      return undefined;
    }

    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    const box = child.box;

    for (let cornerIndex = 0; cornerIndex < 8; cornerIndex++) {
      this.projectedTileCorner
        .set(
          cornerIndex & 1 ? box.max.x : box.min.x,
          cornerIndex & 2 ? box.max.y : box.min.y,
          cornerIndex & 4 ? box.max.z : box.min.z
        )
        .project(camera);

      if (
        !Number.isFinite(this.projectedTileCorner.x) ||
        !Number.isFinite(this.projectedTileCorner.y)
      ) {
        return undefined;
      }

      minX = Math.min(minX, this.projectedTileCorner.x);
      minY = Math.min(minY, this.projectedTileCorner.y);
      maxX = Math.max(maxX, this.projectedTileCorner.x);
      maxY = Math.max(maxY, this.projectedTileCorner.y);
    }

    const isNearScreenEdge =
      minX <= -1 + MISSING_CHILD_COVERAGE_EDGE_NDC_MARGIN ||
      maxX >= 1 - MISSING_CHILD_COVERAGE_EDGE_NDC_MARGIN ||
      minY <= -1 + MISSING_CHILD_COVERAGE_EDGE_NDC_MARGIN ||
      maxY >= 1 - MISSING_CHILD_COVERAGE_EDGE_NDC_MARGIN;

    if (!isNearScreenEdge) {
      return undefined;
    }

    const visibleWidthPixels =
      ((MathUtils.clamp(maxX, -1, 1) - MathUtils.clamp(minX, -1, 1)) / 2) *
      container.clientWidth *
      devicePixelRatio;
    const visibleHeightPixels =
      ((MathUtils.clamp(maxY, -1, 1) - MathUtils.clamp(minY, -1, 1)) / 2) *
      container.clientHeight *
      devicePixelRatio;

    return Math.max(visibleWidthPixels, visibleHeightPixels);
  }

  private collectFallbackTile(node: QMNode, collection: QMNode[]): boolean {
    if (!node.mesh) {
      return false;
    }

    collection.push(node);
    return true;
  }

  public repaint(): void {
    this.mapSettings.updateTextureId();

    this.textureQueues.forEach((queue) => {
      queue.clear();
    });

    const promises: Array<Promise<void>> = [];

    this.tileTreeRoots.forEach((r) => {
      r.traverseAll((node: QMNode) => {
        if (node.mesh && node.isVisibleInFrame) {
          const repaintPromise = node.repaint(this.renderedTiles.has(node));
          promises.push(repaintPromise);
        }
      });
    });

    void Promise.all(promises)
      .then(() => {
        getLogger().debug('Repaint complete');
      })
      .catch((err: unknown) => {
        getLogger().error('Repaint error', err);
      });
  }

  /** Set the base height for the terrain.
   * @param height The new base height, or null to use the default value passed in the
   * settings object.
   * @param options.remesh Whether to rebuild the terrain tiles after changing the base height. If true, disposes and resets all terrain nodes, causing meshes to regenerate.
   * If false, just the height will be updated and the existing meshes will be used.
   */
  public setBaseHeight(
    height: number | null,
    options: { readonly remesh?: boolean } = {}
  ): void {
    if (this.qmDecoder.baseHeight === height) {
      return;
    }
    if (height === null) {
      this.qmDecoder.baseHeight =
        this.mapSettings.options.meshSettings.baseHeight;
    } else {
      this.qmDecoder.baseHeight = height;
    }

    const newHeight = this.qmDecoder.baseHeight ?? 0.0;

    this.mapModeBasePlane?.setFromNormalAndCoplanarPoint(
      new Vector3(0, 0, 1),
      new Vector3(0, 0, newHeight)
    );
    // Skip remesh when the caller already invalidated/reset tiles (e.g. filter
    // Apply: invalidateTerrainData then update height for the next fetch).
    if (options.remesh !== false) {
      this.resetTerrainNodes();
    } else {
      this.redraw();
    }
  }

  /**
   * Drop cached mesh data and force visible tiles to refetch. Used when the
   * terrain filter changes but the model instance is kept (same modelId).
   */
  public invalidateTerrainData(): void {
    // Bump first so in-flight tasks that finish after clear() cannot write
    // results keyed only by tile coordinates into the emptied cache.
    this.qmDecoder.bumpMeshDataGeneration();
    this.qmDataCache.clear();
    this.qmDataPromiseQueue.clear();
    // Flat zoom levels reuse initialTile; drop the previous filter's buffer
    // (restore a caller-supplied blank tile when options provide one).
    this.qmDecoder.initialTile =
      this.mapSettings.tileOptions.initialTile ?? null;
    this.resetTerrainNodes();
  }

  private resetTerrainNodes(): void {
    this.tileTreeRoots.forEach((r) => {
      r.traverseAll((node: QMNode) => {
        node.dispose();
        node.lastTouchedFrame = 0;
        node.state = 'start';
      });
    });
    this.renderedTiles.clear();
    this.updateScheduler.request();
    this.redraw();
  }

  public setOpacity(opacity: number): void {
    for (const r of this.tileTreeRoots) {
      r.traverseAll((node) => {
        this.mapSettings.options.meshSettings.opacity = opacity;
        const material = node.mesh?.material as Web3DMeshMaterial | undefined;
        if (!node.mesh || !material) {
          return;
        }
        material.transparent = opacity < 1;
        material.opacity = opacity;
      });
    }
    this.redraw();
  }

  public async setDisplayMode(mode: 'terrain' | 'map'): Promise<void> {
    if (mode === this.mapSettings.displayMode) {
      return;
    }

    this.mapSettings.displayMode = mode;
    await this.setDisplayModeSmoothly(mode, 300, performance.now());
  }

  private async setDisplayModeSmoothly(
    mode: 'terrain' | 'map',
    duration: number,
    start: number
  ): Promise<void> {
    const now = performance.now();
    const progress = (now - start) / duration;

    this.tileTreeRoots.forEach((rootNode) => {
      rootNode.traverseAll((node: QMNode) => {
        if (node.mesh) {
          node.setDisplayMode(mode, Math.min(progress, 1));
        }
      });
    });
    this.redraw();
    const framePromise = new Promise<void>((resolve) => {
      this.api.renderingManager?.addAnimationFrameListener(() => {
        resolve();
        void this.api.renderingManager?.waitForRender();
      });
    });
    await framePromise;
    if (progress <= 1) {
      await this.setDisplayModeSmoothly(mode, duration, start);
    }
  }

  public override dispose(): void {
    this.disposed = true;
    super.dispose();
    this.qmDataPromiseQueue.clear();
    this.textureQueues.forEach((queue) => queue.clear());
    this.textureQueues.clear();
    this.meshCommitQueue.dispose();
    this.updateScheduler.dispose();
    this.tileTreeRoots.forEach((r) => {
      r.dispose();
    });
    this.tileCache.clear();
    this.textureCache.clear();
    this.qmDataCache.clear();
    this.qmDecoder.dispose();
    if (this.intervalHandle) {
      clearInterval(this.intervalHandle);
    }
    if (this.queueDebugLogIntervalHandle) {
      clearInterval(this.queueDebugLogIntervalHandle);
    }
    window.removeEventListener('click', this.debugHandleMouseClick);
    if (this.mapSettings.options.constrainCamera) {
      this.api.camera?.unsubscribe(this.constrainCameraMovementFn);
    }
    this.api.camera.unsubscribe(this.cameraChangedFn);
    this.api.eventDispatcher?.unsubscribe(
      NAVIGATION_START_EVENT,
      this.navigationStartedFn
    );
    this.api.eventDispatcher?.unsubscribe(
      NAVIGATION_END_EVENT,
      this.navigationEndedFn
    );
  }

  private createTextureCache(imageSize: number): LRUCache<number, ImageBitmap> {
    const maxSize = Math.max(
      this.api.settingsDispatcher!.settings.maxGpuMemoryCache ??
        calculateGpuMemoryCacheSize(),
      1
    );
    return new LRUCache<number, ImageBitmap>({
      maxSize: maxSize * 2,
      sizeCalculation: (_t) => {
        return imageSize * imageSize * 3;
      },
    });
  }

  private createQMDataCache(): LRUCache<number, QuantizedMeshData> {
    return new LRUCache<number, QuantizedMeshData>({
      maxSize: 1024 * 1024 * 200,
      sizeCalculation: (t: QuantizedMeshData) => {
        return approximateQuantizedMeshDataSize(t);
      },
    });
  }

  public raycastHeight(raycaster: Raycaster): number | null {
    const intersections = raycaster.intersectObject(this.root, true);
    if (intersections.length > 0) {
      if (this.mapSettings.displayMode === 'map') {
        return this.qmDecoder.baseHeight ?? 0.0;
      } else {
        return intersections[0].point.z;
      }
    }

    // If no intersection is found, return null to indicate that the raycast did not hit the terrain
    return null;
  }

  public override _setSelection(_ids: number[]): void {
    // No-op, selection not supported
  }

  //@ts-expect-error Interface does not include undefined return, but this is valid
  public pickNavigation(caster: Caster): IIntersection {
    const intersects: IIntersection[] = [];
    let point: Vector3 | null = null;
    let normal: Vector3 | undefined = undefined;
    let face: Face | undefined | null = undefined;
    if (this.mapSettings.displayMode === 'map') {
      const pointOnPlane = new Vector3();
      if (!this.mapModeBasePlane) {
        point = null;
      } else {
        point = caster.ray.intersectPlane(this.mapModeBasePlane, pointOnPlane);
        normal = this.mapModeBasePlane.normal.clone();
      }
    } else {
      const terrainIntersection = caster.intersectObject(this.root);
      if (terrainIntersection.length > 0) {
        point = terrainIntersection[0].point;
        normal =
          terrainIntersection[0].face?.normal.clone() || new Vector3(0, 0, 1);
        face = terrainIntersection[0].face;
      }
    }

    if (point) {
      intersects.push({
        point,
        distance: point.distanceTo(caster.camera.position),
        normal,
        id: 0,
        face: face ?? undefined, // Let null values be coerced to undefined to satisfy the IIntersection interface
        object: this.root,
        model: this,
        caster: caster,
        snapType: SNAP_TYPE_FACE,
      });
    }

    if (intersects.length > 0) {
      const intersection = intersects[0];
      if (intersection.face) {
        const localNormal = intersection.face.normal;
        const object = intersection.object;
        const worldNormal = new Vector3();
        worldNormal.copy(localNormal).transformDirection(object.matrixWorld);
        intersection.normal = worldNormal;
      }

      return intersection;
    }
  }

  public override async pick(caster: Caster): Promise<IIntersection> {
    return this.pickNavigation(caster);
  }

  public override async pickSnapped(
    caster: Caster,
    snapTypes: SnapType[]
  ): Promise<IIntersection[]> {
    if (
      !snapTypes.includes(SNAP_TYPE_FACE as SnapType) ||
      !caster.ray.intersectsBox(this.getModelBoundingBox())
    ) {
      // @ts-expect-error Interface does not include undefined return, but this is valid
      return;
    }

    const intersections = this._intersectModel(caster).map((intersection) => {
      return intersection;
    });

    if (this.mapSettings.displayMode === 'map' && this.mapModeBasePlane) {
      const pointOnPlane = new Vector3();
      if (caster.ray.intersectPlane(this.mapModeBasePlane, pointOnPlane)) {
        const result: IIntersection[] = [];

        if (snapTypes.includes(SNAP_TYPE_FACE as SnapType)) {
          result.push({
            point: pointOnPlane.clone(),
            distance: pointOnPlane.distanceTo(caster.camera.position),
            normal: this.mapModeBasePlane.normal.clone(),
            id: 0,
            object: this.root,
            model: this,
            caster: caster,
            snapType: SNAP_TYPE_FACE,
          } as IIntersection);
        }
        return result;
      }
    }

    return Promise.resolve(intersections);
  }

  public override async areaPick(
    _caster: Caster,
    _containedOnly: boolean
  ): Promise<IIntersection> {
    // @ts-expect-error Interface does not include undefined return, but this is valid
    return;
  }

  public cartographicToPosition(p: Vector2, fromProjection?: string): Vector2 {
    return this.mapSettings.projectPoint(
      p,
      fromProjection ? proj4.Proj(fromProjection) : REFERENCE_SYSTEM,
      this.mapSettings.sceneProj,
      new Vector2()
    );
  }

  public positionToCartographic(p: Vector2, toProjection?: string): Vector2 {
    return this.mapSettings.projectPoint(
      p,
      this.mapSettings.sceneProj,
      toProjection ? proj4.Proj(toProjection) : REFERENCE_SYSTEM,
      new Vector2()
    );
  }

  private readonly debugHandleMouseClick = (event: MouseEvent): void => {
    try {
      // Convert screen coordinates to Three.js normalized device coordinates (-1 to +1)
      this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;

      this.raycaster.setFromCamera(this.mouse, this.api.camera!);
      const intersects = this.raycaster.intersectObject(this.root);

      if (intersects.length > 0) {
        const userData = intersects[0].object.userData as { node?: unknown };
        if (!(userData.node instanceof QMNode)) {
          getLogger().warn(
            '[QuantizedMeshModel] clicked tile did not contain a QMNode',
            intersects[0].object.userData
          );
          return;
        }

        const now = performance.now();
        const queueSnapshot = this.createQueueDebugStateSnapshot(now);
        getLogger().info(
          '[QuantizedMeshModel] clicked tile subtree',
          this.getClickTileSubtreeDebugSnapshot(userData.node, queueSnapshot)
        );
      } else {
        getLogger().info(
          '[QuantizedMeshModel] no tile intersected at click position'
        );
      }
    } catch (error) {
      getLogger().error('[QuantizedMeshModel] error during raycasting', error);
    }
  };

  private updateBoundingBox(): void {
    const boundingBox = new Box3();

    this.tileTreeRoots.forEach((r) => {
      if (r.mesh && r.mesh.geometry.boundingBox) {
        boundingBox.union(r.box);
      }
    });

    const minValues = [];

    if (this.qmDecoder.minHeight !== null) {
      minValues.push(this.qmDecoder.minHeight);
    }

    if (minValues.length !== 0) {
      boundingBox.min.z = Math.min(...minValues);
    }

    boundingBox.max.z = this.qmDecoder.maxHeight ?? boundingBox.max.z;

    this.boundingBox.next(boundingBox);
  }

  public override getGeometry(_ids?: number[]): Geometry[][] {
    const result: Geometry[][] = [];
    this.root.traverse((obj) => {
      if (obj && obj instanceof Mesh) {
        result.push([
          {
            transform: obj.matrix.clone(),
            type: GEOMETRY_TYPE_MESH as GeometryType,
            indices: obj.geometry.index?.array as ArrayLike<number>,
            positions: obj.geometry.attributes.position
              .array as ArrayLike<number>,
            normals: obj.geometry.attributes.normal?.array as ArrayLike<number>,
          },
        ]);
      }
    });
    return result;
  }
}
